/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 20/9/21.
 */
import EventTypes from "@/enums/EventTypes";
import EventStatus from "@/enums/EventStatus";
import ResyncType from "@/enums/ResyncType";

const defaultSequencerSnapshotUpdatedObj = {
	action: undefined,
	state: false
}

export default {
	state: {
		sequencerSnapshotsMap: {},
		mapSequencerByEventId: {},
		sequencerSnapshotsUsageCounterMap: {},
		sequencerSnapshotUpdatedMap: {}
	},

	getters: {
		// Returns the full sequencer snapshot for a given StationID
		getSequencer: (state) => (stationID) => {
			return state.sequencerSnapshotsMap[stationID];
		},

		getSequencerSnapshotUpdated: (state) => (stationID) => {
			return state.sequencerSnapshotUpdatedMap[stationID] || defaultSequencerSnapshotUpdatedObj;
		},

		getSequencerMode: (state) => (stationID) => {
			return state.sequencerSnapshotsMap[stationID]?.SequencerMode;
		},

		getSequencerStatus: (state) => (stationID) => {
			return state.sequencerSnapshotsMap[stationID]?.SequencerStatus;
		},

		getSequencerItems: (state) => (stationID) => {
			const sequencer = state.sequencerSnapshotsMap[stationID];
			return sequencer?.Items?.filter(e => {
				return (
						   (e.EventType === EventTypes.PlayAsset && e.AssetID)
						   || e.EventType === EventTypes.SpotBlock ||
						   e.EventType === EventTypes.LocalBreak ||
						   e.EventType === EventTypes.DVTBlock ||
						   e.EventType === EventTypes.MusicFillPos
					   ) &&
					   e.ResyncType !== ResyncType.Full && // Don't show PlaylistEvents that will be dropped due to ETM
					   !e.Skipped && // Don't show Skipped item(s)
					   [EventStatus.Ready, EventStatus.Current, EventStatus.Paused].includes(e.StatusCode); // Don't display the Played item(s)
			}) ?? [];
		},

		getSequencerItemByEventID: (state) => (stationID, eventId) => {
			return state.mapSequencerByEventId[stationID]?.[eventId];
		},

		getSequencerShowEpisodeID: (state) => (stationID) => {
			return state.sequencerSnapshotsMap[stationID]?.ShowEpisodeID;
		}
	},

	mutations: {
		setSequencer(state, sequencer) {
			state.sequencerSnapshotsMap[sequencer.StationID] = sequencer;

			if (!state.sequencerSnapshotsUsageCounterMap[sequencer.StationID]) {
				state.sequencerSnapshotsUsageCounterMap[sequencer.StationID] = 0;
			}
			state.sequencerSnapshotsUsageCounterMap[sequencer.StationID]++;
		},

		removeSequencer(state, stationID) {
			if (state.sequencerSnapshotsUsageCounterMap[stationID] === 1) {
				delete state.sequencerSnapshotsMap[stationID];
				delete state.mapSequencerByEventId[stationID];
			}
			state.sequencerSnapshotsUsageCounterMap[stationID]--;
		},

		setSequencerItemByEventID(state, {stationID, item}) {
			if (!state.mapSequencerByEventId[stationID]) {
				state.mapSequencerByEventId[stationID] = {};
			}
			state.mapSequencerByEventId[stationID][item.EventID] = item;
		},

		deleteSequencerItemByEventID(state, {stationID, item}) {
			delete state.mapSequencerByEventId[stationID]?.[item.EventID];
		},

		setSequencerSnapshotUpdated(state, {stationID, status}) {
			state.sequencerSnapshotUpdatedMap[stationID] = status;
		},

		resetSequencerState(state) {
			state.sequencerSnapshotsMap = {};
			state.mapSequencerByEventId = {};
			state.sequencerSnapshotsUsageCounterMap = {};
			state.sequencerSnapshotUpdatedMap = {};
		}
	},

	actions: {
		sequencerSnapshotResponse({getters, commit, dispatch}, sequencer) {
			sequencer?.Items?.forEach(item => {
				dispatch("formatSequencerEvent", {event: item, stationID: sequencer.StationID});
				item.ChildItems?.forEach(childItem => {
					dispatch("formatSequencerEvent", {event: childItem, stationID: sequencer.StationID});
				});
			});
			// Store snapshot under StationID in the map
			commit("setSequencer", sequencer);

			let action = getters.getSequencerSnapshotUpdated(sequencer.StationID).action;
			if (action) {
				commit(
					"setSequencerSnapshotUpdated",
					{
						stationID: sequencer.StationID,
						status: {action, state: false}
					}
				);
			}
			if (!sequencer.Items || sequencer.Items.length === 0) {
				commit("SHOW_NOTIFICATION_WITH_TYPE", {message: "No items are being played."});
			}
		},

		formatSequencerEvent({commit}, {event, stationID}) {
			const duration = event?.DurationToSegue ?? 0;
			const Elapsed = event?.Elapsed ?? 0;
			const intro = event?.MaxIntro ?? 0;
			const RemainingTime = duration >= Elapsed ? (duration - Elapsed) : 0;
			const RemainingIntro = Elapsed && (intro + 1) >= Elapsed ? (intro + 1 - Elapsed) : intro;

			commit(
				"setSequencerItemByEventID",
				{stationID, item: {...event, Elapsed, RemainingTime, RemainingIntro}}
			);
		}
	}
};
